import { createFileRoute, Link } from "@tanstack/react-router";
import { ProsePage } from "../components/prose-page.tsx";

export const Route = createFileRoute("/privacy")({ component: PrivacyPage });

/**
 * The privacy policy.
 *
 * Written from what the system actually stores — the `user`, `session`,
 * `business` and `audit_log` tables — rather than from a template. A policy
 * that describes data collection the product does not do is worse than none:
 * it is a promise nobody can check.
 */
function PrivacyPage() {
	return (
		<ProsePage
			title="Privacy"
			intro="We hold as little as the service can work with, and we do not sell any of it."
			updated="13 August 2026"
		>
			<h2>If you are looking for work</h2>
			<p>
				You need no account, and we ask for nothing. Browsing the board and
				opening a listing does not create a record about you, and applications
				go directly to the employer — we never see them. There is no tracking
				pixel, no advertising network, and no third-party analytics on this
				site.
			</p>

			<h2>If you have an account</h2>
			<p>We store what an account needs to work:</p>
			<ul>
				<li>Your name and email address.</li>
				<li>
					A cryptographic hash of your password — never the password itself.
				</li>
				<li>
					Your active sessions, each with the IP address and browser they were
					created from, so you can recognise and revoke one you do not know.
				</li>
				<li>Which businesses you belong to, and your role in each.</li>
			</ul>

			<h2>If you register a business</h2>
			<p>
				Whatever you enter about the business — name, description, county, city,
				website, contact email and phone. Everything except the contact details
				appears publicly once approved, and the contact details are shown to the
				JDA reviewer.
			</p>

			<h2>Our record of decisions</h2>
			<p>
				Approvals, suspensions, removals and role changes are recorded
				permanently, with who made the decision and when. That record is what
				lets us explain a decision months later, and it is visible only to
				platform administrators.
			</p>

			<h2>Email</h2>
			<p>
				We send transactional email only: confirming your address, resetting
				your password, telling you a decision has been made, warning you a
				listing is about to close. There is no newsletter and no marketing list.
			</p>

			<h2>Who else sees it</h2>
			<p>
				Nobody, beyond the service providers that run the platform — our host,
				our database and our email provider — and only to the extent needed to
				deliver it. We do not sell, rent or trade personal data.
			</p>

			<h2>Your choices</h2>
			<ul>
				<li>Change your name or password at any time in account settings.</li>
				<li>Sign out of every device from the same place.</li>
				<li>
					Ask us to delete your account and we will, unless you are the last
					owner of a business — hand that over first.
				</li>
				<li>
					Ask what we hold about you, at{" "}
					<a href="mailto:support@nwgaconnect.local">
						support@nwgaconnect.local
					</a>
					.
				</li>
			</ul>

			<h2>Cookies</h2>
			<p>
				One, and only when you sign in: the cookie that keeps you signed in. It
				is marked
				<code> HttpOnly</code> and <code> SameSite</code>, and is secure in
				production. There are no advertising or analytics cookies.
			</p>

			<p>
				See also the <Link to="/terms">terms of use</Link>.
			</p>
		</ProsePage>
	);
}
